import React, { useState, useCallback, useRef, useEffect } from "react"
import cn from "classnames"
import SVGInline from "react-svg-inline"
import { useTranslation } from "react-i18next"
import { sizeUnit } from "@/common/locales"
import SignPreview, { useSignPreview, type SignPreviewOptions } from "@/common/components/sign_preview"
import { fixmethodName, type BasketItem } from "@/checkout/components/basket/items"
import { TrashOutline, EditOutline, BundleOutline } from "@/skylticons"
import { debounce } from "lodash-es"
import ClientData from "@/common/client_data"
import { type FragmentType, getFragment, graphql } from "@/api"
import type { BasketItem_OrderItemFragment } from "@/api/graphql"
import { useMutation } from "@apollo/client"
import type { TFunction } from "i18next"
import { isBlank } from "@/common/util"
import ConfirmLink from "@/common/components/confirm_link"
import { MinusCircleIcon, PlusCircleIcon } from "@heroicons/react/24/solid"
import DotSpinner from "@/common/components/dot_spinner"
import env from "@/common/env"
import storageLocal from "@/common/storage"

export const BasketItemFragmentDoc = graphql(`
  fragment BasketItem_OrderItem on OrderItem {
    id
    width
    height
    localPrice
    localTaxedPrice
    localTotal
    localTaxedTotal
    priceSek
    previewSvg
    previewSvgB
    articleType
    signType
    totalCount
    hangType
    description
    colors
    materialTr
    additionalProduct
    metaProduct
    clientDescription
    customerEdit
    regularLocalTotal
    regularTaxedTotal
    hasDiscount
    customProduct
    customProductAttributes
    bundleItem
    exampleFixedPrice
    materialNameForGtm
    discountPercentage
    exampleId
    sizeDisplay
    discontinued

    bundle {
      id

      image {
        id
        webp
        png
      }
    }
  }
`)

const countUpdateDoc = graphql(`
  mutation BasketCountUpdate($id: ID!, $count: Int!) {
    basketCountUpdate(id: $id, count: $count) {
      success

      item {
        totalCount
        ...BasketItem_OrderItem
      }

      status {
        ...BasketModal_Order
      }
    }
  }
`)

const deleteDoc = graphql(`
  mutation BasketDelete($id: ID!) {
    basketDelete(id: $id) {
      success

      status {
        ...BasketModal_Order
      }
    }
  }
`)

const size = (item: BasketItem_OrderItemFragment) => {
  if (!item.width && !item.height) {
    return ""
  }

  return `${item.width || 0}x${item.height || 0}`
}

const editLink = (t: TFunction, item: BasketItem_OrderItemFragment) => {
  if (item.exampleFixedPrice) {
    if (confirm(t("fixed_price.dialog_text")) == true) {
      window.location.href = `/order/edit_id/${item.id}`
    }
  } else {
    window.location.href = `/order/edit_id/${item.id}`
  }
}

interface ItemSpecsProps {
  desktop: boolean
  item: FragmentType<typeof BasketItemFragmentDoc>
  signCode: boolean
  businessRole: boolean
}

const ItemSpecs: React.FC<ItemSpecsProps> = ({ item: itemFragment, desktop, signCode, businessRole }) => {
  const item = getFragment(BasketItemFragmentDoc, itemFragment)
  const { t } = useTranslation()

  const text = []

  if (desktop) {
    text.push(`${item.totalCount} ${item.totalCount > 1 ? t("checkout.items") : t("order.item")}`)
  }

  if (!item.additionalProduct && !item.bundle) {
    if (signCode || item.customProduct) {
      text.push(`${size(item)} ${sizeUnit}`)
    }

    if (!isBlank(item.colors)) {
      text.push(item.colors)
    }

    if (!isBlank(fixmethodName(item as BasketItem, t))) {
      text.push(fixmethodName(item as BasketItem, t))
    }

    if (item.totalCount > 1) {
      text.push(
        <span className="item-price">
          {`${businessRole ? item.localPrice : item.localTaxedPrice} ${t("misc.per_item")}`}
        </span>
      )
    }
  }

  return (
    <div className="item-specs paragraph-xs mb-0 mt-1 text-xs xl:mt-0 xl:text-sm">
      {text.map((item, i) => {
        return (
          <React.Fragment key={i}>
            {desktop ? (
              <>
                {i > 0 && ", "}
                {item}
              </>
            ) : (
              <div>{item}</div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

interface BasketSignItemProps {
  desktop: boolean
  index: number
  item: FragmentType<typeof BasketItemFragmentDoc>
  businessRole: boolean
  className?: string
  activePage: string
  numberOfProducts?: number
  isLastItem: boolean
  setOffset: (offset: number) => void
}

interface BasketItemWrappedProps extends BasketSignItemProps {
  deleteItem: () => void
  loading: boolean
  countLoading: boolean
  count: string
  currentCount: number
  countOp: (op: "plus" | "minus") => void
  countChange: (value: string) => void
  openPreview: (options: SignPreviewOptions) => void
  signCode: boolean
  name?: string | null
  svgs: string[]
  editable: boolean
  bundleItem: boolean
  isLastItem: boolean
  setCount: (value: string) => void
}

const BasketItem: React.FC<BasketItemWrappedProps> = ({
  item: itemFragment,
  deleteItem,
  loading,
  count,
  countOp,
  countChange,
  openPreview,
  signCode,
  name,
  svgs,
  editable,
  bundleItem,
  businessRole,
  desktop,
  className,
  countLoading,
  setCount,
  numberOfProducts,
  index,
  setOffset,
  isLastItem,
  activePage,
}) => {
  const item = getFragment(BasketItemFragmentDoc, itemFragment)
  const { t } = useTranslation()
  const switchBackground =
    (item.signType === "vinyl_text" || item.signType === "contour_cut") && item.colors === "White"
  const bundleSvg = bundleItem && item.previewSvg
  const currentCount = parseInt(count)
  const showBorder = numberOfProducts && numberOfProducts > 1

  const editedItem = storageLocal.getItem("edited_item_id") === item.id
  const editedItemRow = useRef<HTMLDivElement | null>(null)
  const itemsContainer = document.querySelector<HTMLDivElement>(".items-table")

  useEffect(() => {
    if (!editedItemRow.current) {
      return
    }

    const isElementVisible = () => {
      const element = editedItemRow.current?.getBoundingClientRect()
      const container = itemsContainer?.getBoundingClientRect()

      if (!element || !container) {
        return
      }

      return (
        element.top >= container.top &&
        element.left >= container.left &&
        element.bottom <= container.bottom &&
        element.right <= container.right
      )
    }

    if (editedItem && !isElementVisible()) {
      editedItemRow.current.scrollIntoView({ behavior: "instant" })
    }
  }, [editedItemRow, editedItem, itemsContainer])

  const actionsBlock = () => {
    return (
      <div className="mt-1 flex items-center justify-between xl:mt-0">
        <div className="item-buttons flex">
          {signCode && editable && (
            <button
              className={cn("edit_sign_link button button-subtle mb-0 flex", {
                "button-small": desktop,
                "button-large": !desktop,
              })}
              onClick={() => editLink(t, item)}
              type="button"
            >
              <EditOutline className="!mr-0 xl:!mr-1" />

              <span className="paragraph-2xs mb-0 ml-1 font-semibold xl:ml-0 xl:text-sm">{t("misc.edit")}</span>
            </button>
          )}
          {isLastItem && activePage === "checkout" ? (
            <ConfirmLink
              href={`/order/remove_id/${item.id}`}
              className={cn("remove_sign_link button button-subtle mb-0 flex xl:!ml-1", {
                "button-small": desktop,
                "button-large icon-only": !desktop,
              })}
            >
              <TrashOutline className="!mr-0 xl:!mr-1" />
              {desktop && <span className="paragraph-sm mb-0 xl:text-sm xl:font-semibold">{t("misc.delete")}</span>}
            </ConfirmLink>
          ) : (
            <div className="flex items-center">
              <button
                className={cn("basket_remove_button button button-subtle mb-0 flex xl:!ml-1", {
                  "button-small": desktop,
                  "button-large icon-only": !desktop,
                })}
                onClick={() => {
                  setOffset(Math.max(0, index - 15))
                  deleteItem()
                }}
                type="button"
                disabled={loading}
              >
                <TrashOutline className="!mr-0 xl:!mr-1" />
                {desktop && <span className="paragraph-sm mb-0 xl:text-sm xl:font-semibold">{t("misc.delete")}</span>}
              </button>
              {loading && <DotSpinner />}
            </div>
          )}
        </div>
        <div className="count flex items-center justify-center xl:-mr-3.5">
          {countLoading && <DotSpinner />}
          <button
            className="count-minus button button-subtle button-large icon-only mb-0 mr-1 w-12"
            disabled={currentCount < 2 || countLoading}
            onClick={() => countOp("minus")}
            type="button"
          >
            <MinusCircleIcon className="fill-ink-100 hover:fill-ink-85" />
          </button>
          <input
            type="text"
            name="basket_item_count"
            className="article_count h-10 w-12 border-white-20 px-1.5 text-center"
            value={count}
            onChange={e => countChange(e.target.value)}
            onFocus={e => e.currentTarget.select()}
            onBlur={e => {
              if (e.currentTarget.value === "") {
                setCount(item.totalCount.toString())
              }
            }}
            disabled={countLoading}
            inputMode="numeric"
          />
          <button
            className="count-plus button button-subtle button-large icon-only mb-0 ml-1 w-12"
            type="button"
            disabled={countLoading}
            onClick={() => countOp("plus")}
          >
            <PlusCircleIcon className="box-content fill-ink-100 hover:fill-ink-85" />
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      key={item.id}
      className={cn(
        "item product-row desktop flex h-full w-full flex-col pb-3 pl-4 pr-1 pt-4 xl:flex-row xl:px-0 xl:pr-4",
        className,
        {
          loading,
          "border-b border-white-10": showBorder,
        }
      )}
      ref={editedItemRow}
    >
      <div className="flex w-full">
        {(signCode || bundleSvg) && (
          <div
            className={cn(
              "sign mr-2 h-[88px] w-[88px] min-w-[88px] cursor-pointer overflow-hidden rounded border border-white-20 bg-white-5 xl:mr-4 xl:h-[106px] xl:min-w-40",
              {
                "bg-white-10": switchBackground,
              }
            )}
          >
            <span
              className="h-full w-full"
              onClick={() =>
                openPreview({
                  svg: svgs,
                  transparent: true,
                  withBackground: true,
                  signWidth: item.width || 0,
                  signHeight: item.height || 0,
                  signType: item.signType || "",
                  metaProduct: item.metaProduct || "",
                })
              }
            >
              {item.previewSvgB ? (
                <>
                  <SVGInline
                    component="div"
                    svg={item.previewSvg || ""}
                    className="svg-wrapper data-hj-suppress flex h-1/2 w-full items-center justify-center p-2 pb-1"
                  />
                  <SVGInline
                    component="div"
                    svg={item.previewSvgB || ""}
                    className="svg-wrapper data-hj-suppress flex h-1/2 w-full items-center justify-center p-2 pt-1"
                  />
                </>
              ) : (
                <SVGInline
                  component="div"
                  svg={item.previewSvg || ""}
                  className="svg-wrapper data-hj-suppress flex h-full w-full items-center justify-center p-2"
                />
              )}
            </span>
          </div>
        )}
        {bundleItem && !bundleSvg && (
          <div className="mr-2 flex h-[88px] w-[88px] min-w-[88px] justify-center rounded border border-white-20 bg-white-5 xl:h-[106px] xl:min-w-40">
            {item.bundle?.image?.png ? (
              <img src={item.bundle.image.png} className="h-full p-2" />
            ) : (
              <BundleOutline className="h-16 w-16" />
            )}
          </div>
        )}
        {!(signCode || bundleItem) && <div className="sign" />}
        <div className="flex w-full flex-col xl:justify-between">
          <div className="flex justify-between">
            <div className="flex flex-col">
              <div className="flex justify-between">
                <div className="name paragraph-sm bold mb-0 whitespace-nowrap text-sm xl:text-base">
                  {name}
                  {item.previewSvgB && ` ${item.materialTr?.toLowerCase()}`}
                </div>
              </div>
              <ItemSpecs desktop={desktop} item={itemFragment} signCode={signCode} businessRole={businessRole} />
            </div>
            <div className="total flex flex-col items-end pr-3 xl:pr-0">
              <div className="price paragraph-sm current mb-0 whitespace-nowrap text-right text-sm font-semibold xl:text-base">
                {businessRole ? item.localTotal : item.localTaxedTotal}
              </div>
              {item.hasDiscount && (
                <div className="discount regular whitespace-nowrap pl-1 text-sm text-ink-50 line-through">
                  {businessRole ? item.regularLocalTotal : item.regularTaxedTotal}
                </div>
              )}
            </div>
          </div>
          {desktop && actionsBlock()}
        </div>
      </div>
      {!desktop && actionsBlock()}
    </div>
  )
}

const BasketSignItem: React.FC<BasketSignItemProps> = props => {
  const item = getFragment(BasketItemFragmentDoc, props.item)
  const { t } = useTranslation()
  const [count, setCount] = useState(item.totalCount.toString())
  const admin = !!(ClientData.admin && ClientData.admin.id)
  const [countChangeTriggered, setCountChangeTriggered] = useState(false)

  const [countMutation, countMutationResult] = useMutation(countUpdateDoc, {
    onCompleted: data => {
      if (data && data.basketCountUpdate && data.basketCountUpdate.item) {
        setCount(data.basketCountUpdate.item.totalCount.toString())
        props.setOffset(Math.max(0, props.index - 15))
      }
    },
  })

  const [deleteMutation, deleteMutationResult] = useMutation(deleteDoc, {
    variables: { id: item.id },
  })

  const debouncedCountMutation = useRef(debounce(countMutation, env.TEST ? 0 : 800, { leading: false }))

  const countOp = useCallback(
    (op: "plus" | "minus") => {
      setCountChangeTriggered(true)

      const current = parseInt(count, 0) || 0
      const newCount = op === "plus" ? current + 1 : current - 1
      setCount(newCount.toString())
      debouncedCountMutation.current({ variables: { id: item.id, count: newCount } })

      setCountChangeTriggered(false)
    },
    [count, setCount, item.id]
  )

  const countChange = useCallback(
    (value: string) => {
      setCountChangeTriggered(true)
      const newCount = parseInt(value)

      if (value === "") {
        setCount("")
      } else if (value === "0") {
        return
      } else if (!isNaN(newCount)) {
        setCount(newCount.toString())
        debouncedCountMutation.current({
          variables: { id: item.id, count: Math.max(Math.min(newCount, 10000), 1) },
        })
      }

      setCountChangeTriggered(false)
    },
    [setCount, item.id]
  )

  const [openPreview, previewProps] = useSignPreview()

  const currentCount = parseInt(count)
  const signCode = item.articleType === "sign_code"
  const bundleItem = item.articleType === "bundle_item"

  const name = signCode
    ? item.metaProduct
      ? t(`sign_type.${item.metaProduct}`)
      : t(`sign_type.${item.signType}`)
    : item.clientDescription

  const svgs: string[] = []

  if (item.previewSvg) {
    svgs.push(item.previewSvg)
  }

  if (item.previewSvgB) {
    svgs.push(item.previewSvgB)
  }

  const itemProps = {
    ...props,
    deleteItem: deleteMutation,
    loading: deleteMutationResult.loading,
    countLoading: countMutationResult.loading || countChangeTriggered,
    count,
    countOp,
    countChange,
    openPreview,
    signCode,
    name,
    svgs,
    currentCount,
    editable: item.customerEdit || admin,
    bundleItem,
    setCount,
    numberOfProducts: props.numberOfProducts,
  }

  return (
    <>
      <BasketItem {...itemProps} />
      <SignPreview {...previewProps} />
    </>
  )
}

export default BasketSignItem
